import React from "react";
import styled, { keyframes } from "styled-components";
import { useGoalList } from "../Register/GoalListContext";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFire, faHeart, faFolder, faMagnifyingGlass, faArrowLeft } from "@fortawesome/free-solid-svg-icons";

/* ── アニメーション ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

/* ── レイアウト ── */
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(-45deg, #0f0c29, #302b63, #24243e, #1a1a2e);
  background-size: 400% 400%;
  animation: ${gradientShift} 12s ease infinite;
  padding: 40px 16px 60px;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: clamp(1.4rem, 4vw, 2rem);
  font-weight: 900;
  background: linear-gradient(90deg, #ffd700, #ff8c00, #ffd700);
  background-size: 200% auto;
  animation: ${shimmer} 3s linear infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const PageSub = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: clamp(0.8rem, 2vw, 0.95rem);
  margin-bottom: 32px;
`;

const ContentArea = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

/* ── 結果カード ── */
const ResultCard = styled.div`
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.25s;
  animation: ${fadeInUp} 0.5s ease ${(props) => 0.05 * props.$index}s both;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 28px rgba(102, 126, 234, 0.25);
    border-color: rgba(255, 215, 0, 0.3);
  }
`;

const CardRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.h3`
  font-size: clamp(1rem, 2.5vw, 1.15rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardDesc = styled.p`
  font-size: clamp(0.8rem, 2vw, 0.9rem);
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.6;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 50px;
  background: ${(props) => props.$bg || "rgba(255, 255, 255, 0.08)"};
  border: 1px solid ${(props) => props.$border || "rgba(255, 255, 255, 0.12)"};
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(props) => props.$color || "rgba(255, 255, 255, 0.6)"};
`;

const GoalImage = styled.img`
  width: 110px;
  height: 80px;
  object-fit: cover;
  border-radius: 10px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: 600px) {
    width: 100%;
    max-width: 280px;
    height: auto;
  }
`;

/* ── 空状態 ── */
const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  animation: ${fadeInUp} 0.6s ease both;
`;

const EmptyIconWrap = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const EmptyText = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: clamp(0.9rem, 2.5vw, 1.05rem);
  line-height: 1.8;
`;

/* ── ボタン ── */
const BackBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 32px auto 0;
  padding: 14px 36px;
  border-radius: 50px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 700;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  box-sizing: border-box;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  animation: ${fadeInUp} 0.6s ease 0.3s both;
  @media (max-width: 480px) {
    width: 100%;
    max-width: 320px;
    padding: 14px 24px;
  }
  &:hover {
    border-color: #fff;
    color: #fff;
    transform: translateY(-2px);
  }
`;

const ResultCount = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  margin-bottom: 24px;
  animation: ${fadeInUp} 0.5s ease both;
`;

/* ── コンポーネント ── */
const RegisterSearchResult = () => {
  const { goals } = useGoalList();
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <PageTitle>検索結果</PageTitle>
      <PageSub>見つかった無謀な夢たち</PageSub>

      <ContentArea>
        {goals.length > 0 ? (
          <>
            <ResultCount>{goals.length} 件の夢が見つかりました</ResultCount>
            {goals.map((goal, i) => (
              <ResultCard
                key={goal.id || goal.goalID}
                $index={i}
                onClick={() => navigate(`/boards/${goal.id}`)}
              >
                <CardRow>
                  <CardBody>
                    <CardTitle>{goal.title}</CardTitle>
                    <CardDesc>
                      {goal.description || "目標の詳細がここに表示されます。"}
                    </CardDesc>
                    <CardMeta>
                      {goal.no_way_count != null && (
                        <MetaBadge
                          $color="rgba(244,100,80,0.95)"
                          $bg="rgba(244,63,94,0.1)"
                          $border="rgba(244,63,94,0.2)"
                        >
                          <FontAwesomeIcon icon={faFire} size="xs" />
                          {goal.no_way_count}
                        </MetaBadge>
                      )}
                      {goal.support_count != null && (
                        <MetaBadge
                          $color="rgba(16,185,129,0.95)"
                          $bg="rgba(16,185,129,0.1)"
                          $border="rgba(16,185,129,0.2)"
                        >
                          <FontAwesomeIcon icon={faHeart} size="xs" />
                          {goal.support_count}
                        </MetaBadge>
                      )}
                      {goal.category && (
                        <MetaBadge>
                          <FontAwesomeIcon icon={faFolder} size="xs" />
                          {goal.category}
                        </MetaBadge>
                      )}
                    </CardMeta>
                  </CardBody>
                  {goal.image_url && (
                    <GoalImage src={goal.image_url} alt={goal.title} />
                  )}
                </CardRow>
              </ResultCard>
            ))}
          </>
        ) : (
          <EmptyState>
            <EmptyIconWrap>
              <FontAwesomeIcon icon={faMagnifyingGlass} color="rgba(255,255,255,0.3)" size="xl" />
            </EmptyIconWrap>
            <EmptyText>
              まだ検索結果がありません。
              <br />
              キーワードやカテゴリを変えて、
              <br />
              無謀な夢を探してみよう。
            </EmptyText>
          </EmptyState>
        )}

        <BackBtn onClick={() => navigate("/RegisterSearchView")}>
          <FontAwesomeIcon icon={faArrowLeft} size="sm" />
          検索画面に戻る
        </BackBtn>
      </ContentArea>

      <ToastContainer position="top-center" autoClose={3000} />
    </PageWrapper>
  );
};

export default RegisterSearchResult;
